import React, { createContext, useContext, useReducer } from "react";

const MAXIMUM_INSTANCE_COUNT = 3;

type Weedfetti = { id: string; colors?: string[] };

type WeedfettiAction =
  | { type: "cleanup"; value: string }
  | { type: "launch"; value: { colors?: string[] } };

export const WeedfettiContext = createContext<{
  cleanup: (id: string) => void;
  instances: Weedfetti[];
  launchWeedfetti: (options?: { colors?: string[] }) => void;
}>({
  cleanup: () => {},
  instances: [],
  launchWeedfetti: () => {},
});
WeedfettiContext.displayName = "WeedfettiContext";

export const useWeedfetti = () => useContext(WeedfettiContext);

export const WeedfettiProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const [instances, dispatch] = useReducer(
    (state: Weedfetti[], action: WeedfettiAction) => {
      switch (action.type) {
        case "launch": {
          if (state.length >= MAXIMUM_INSTANCE_COUNT) {
            return state;
          } else {
            const id = createId();
            return [...state, { id, ...action.value }];
          }
        }
        case "cleanup":
          return state.filter((instance) => instance.id !== action.value);
        default:
          return state;
      }
    },
    []
  );

  return (
    <WeedfettiContext.Provider
      value={{
        cleanup: (id) => {
          dispatch({ type: "cleanup", value: id });
        },
        instances,
        launchWeedfetti: ({ colors } = {}) => {
          dispatch({ type: "launch", value: { colors } });
        },
      }}
    >
      {children}
    </WeedfettiContext.Provider>
  );
};

function createId() {
  // Creates a random ten letter string for use as a unique-ish ID
  return Math.random().toString(36).substring(10);
}
